import { getAccessToken } from "@/lib/api";

/**
 * Minimal WS helper for chat.
 *
 * Staff WS:
 *   /ws/b/<businessId>/chat/<appointmentOrThreadId>/?token=<access_jwt>
 *
 * Public WS:
 *   /ws/public/chat/<client_token>/
 */

function wsBase() {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}`;
}

function buildUrl(path, params) {
  const url = new URL(path, wsBase());
  if (params && typeof params === "object") {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      url.searchParams.set(k, String(v));
    });
  }
  return url.toString();
}

function connectWithReconnect(url, { onEvent, onOpen, onClose, debugLabel } = {}) {
  let ws = null;
  let closedByUser = false;
  let retry = 0;
  let retryTimer = null;

  const log = (...args) => {
    if (!debugLabel) return;
    // eslint-disable-next-line no-console
    console.log(`[ws:${debugLabel}]`, ...args);
  };

  const scheduleReconnect = () => {
    if (closedByUser) return;
    retry += 1;
    const ms = Math.min(10000, 500 * retry); // 0.5s -> 10s cap
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      start();
    }, ms);
  };

  const start = () => {
    if (closedByUser) return;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      retry = 0;
      log("open");
      onOpen && onOpen();
    };

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        onEvent && onEvent(data);
      } catch {
        // ignore non-json
      }
    };

    ws.onclose = (ev) => {
      log("close", ev?.code, ev?.reason);
      onClose && onClose(ev);
      scheduleReconnect();
    };

    ws.onerror = () => {
      // browser will also call onclose; keep quiet
    };
  };

  start();

  return {
    close() {
      closedByUser = true;
      try {
        clearTimeout(retryTimer);
      } catch {}
      try {
        ws && ws.close();
      } catch {}
    },
  };
}

export function connectStaffChatSocket({
  businessId,
  appointmentOrThreadId,
  onEvent,
  onOpen,
  onClose,
  debugLabel,
} = {}) {
  const token = getAccessToken();
  const url = buildUrl(
    `/ws/b/${businessId}/chat/${appointmentOrThreadId}/`,
    token ? { token } : {}
  );
  return connectWithReconnect(url, { onEvent, onOpen, onClose, debugLabel: debugLabel || "staff-chat" });
}

export function connectPublicChatSocket({ token, onEvent, onOpen, onClose, debugLabel } = {}) {
  const url = buildUrl(`/ws/public/chat/${token}/`);
  return connectWithReconnect(url, { onEvent, onOpen, onClose, debugLabel: debugLabel || "public-chat" });
}
